import { useSelector, useDispatch } from 'react-redux'
import { useCallback, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { onAuthStateChanged } from 'firebase/auth'
import { useAppDispatch, useAppSelector, store } from '../store/index'
import type { RootState } from '../store'
import { 
  loginStart, 
  loginSuccess, 
  loginFailure, 
  logout as logoutAction, 
  updateUser, 
  addXP,
  setLoading,
  setError,
  clearError
} from '../store/slices/authSlice'
import { authService, userService, type FirebaseUser } from '../services/firebase'
import { auth } from '../config/firebase'
import { setLocalStorage, removeLocalStorage, getLocalStorage, debounce } from '../utils/helpers'
import { ROUTES } from '../utils/constants'
import { REHYDRATE } from 'redux-persist'
import type { User } from '../types'

interface UseAuthReturn {
  // State
  user: User | null
  isAuthenticated: boolean
  loading: boolean
  error: string | null
  
  // Actions
  login: (email: string, password: string) => Promise<void>
  register: (userData: {
    name: string
    email: string
    password: string
    grade: number
  }) => Promise<void>
  logout: () => Promise<void>
  updateProfile: (userData: Partial<User>) => Promise<void>
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>
  forgotPassword: (email: string) => Promise<void>
  resetPassword: (token: string, password: string) => Promise<void>
  verifyEmail: (token: string) => Promise<void>
  refreshToken: () => Promise<void>
  addUserXP: (amount: number, reason?: string) => void
  clearAuthError: () => void
  
  // Utilities
  hasPermission: (permission: string) => boolean
  hasRole: (role: string) => boolean
  isEmailVerified: () => boolean
  getAuthToken: () => Promise<string | null>
  checkAuthStatus: () => void
}

const useAuth = (): UseAuthReturn => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  
  const { user, isAuthenticated, loading, error } = useSelector(
    (state: RootState) => state.auth
  )

  // Login function
  const login = useCallback(async (email: string, password: string): Promise<void> => {
    try {
      dispatch(loginStart())
      
      // Firebase authentication
      const firebaseUser = await authService.login(email, password)
      
      // Get user data from Firestore
      let userData = await userService.getUserData(firebaseUser.uid)

      // Ensure user document exists if database is empty or user doc missing
      if (!userData) {
        try {
          userData = await userService.ensureUserDocument(firebaseUser.uid)
        } catch (e) {
          // no-op, will handle below
        }
      }
      
      if (userData) {
        // Prepare roles consistently from Firestore data
        const normalizedRoles = Array.isArray(userData.roles)
          ? userData.roles
          : (userData.role ? [userData.role] : ['user'])

        // Convert Firebase user to app user format with serializable data
        const appUser: User = {
          id: userData.id,
          name: userData.name,
          email: userData.email,
          avatar: userData.avatar,
          level: userData.level,
          xp: userData.xp,
          coins: userData.coins,
          badges: userData.badges,
          grade: userData.grade,
          school: userData.school,
          createdAt: typeof userData.createdAt === 'object' && userData.createdAt?.toDate ? userData.createdAt.toDate().toISOString() : userData.createdAt,
          lastLoginAt: typeof userData.lastLoginAt === 'object' && userData.lastLoginAt?.toDate ? userData.lastLoginAt.toDate().toISOString() : userData.lastLoginAt,
          preferences: userData.preferences,
          subscription: userData.subscription,
          stats: userData.stats,
          // Ensure both role and roles are present for authorization checks
          role: userData.role,
          roles: normalizedRoles,
          targetScore: userData.targetScore || 0,
          emailVerified: userData.emailVerified || false
        } as User
        
        // Store auth data
        setLocalStorage('auth_token', firebaseUser.uid)
        setLocalStorage('user_data', appUser)
        
        dispatch(loginSuccess({
          user: appUser,
          token: firebaseUser.uid,
          refreshToken: firebaseUser.uid
        }))
        
        toast.success(`Hoş geldin, ${userData.name}!`)
        
        // Redirect to dashboard
        navigate(ROUTES.DASHBOARD)
      } else {
        throw new Error('Kullanıcı verisi bulunamadı')
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Giriş sırasında bir hata oluştu'
      dispatch(loginFailure(errorMessage))
      toast.error(errorMessage)
      throw error
    }
  }, [dispatch, navigate])

  // Register function
  const register = useCallback(async (userData: {
    name: string
    email: string
    password: string
    grade: number
  }): Promise<void> => {
    try {
      dispatch(setLoading(true))
      
      // Firebase registration
      const { user: firebaseUser, userData: firebaseUserData } = await authService.register(
        userData.email,
        userData.password,
        {
          name: userData.name,
          grade: `${userData.grade}. Sınıf`
        }
      )
      
      // Convert Firebase user to app user format with serializable data
      const appUser: User = {
        ...firebaseUserData,
        id: firebaseUser.uid,
        roles: firebaseUserData.roles || ['user'], // Admin role desteği
        createdAt: typeof firebaseUserData.createdAt === 'object' && firebaseUserData.createdAt?.toDate ? firebaseUserData.createdAt.toDate().toISOString() : firebaseUserData.createdAt,
        lastLoginAt: typeof firebaseUserData.lastLoginAt === 'object' && firebaseUserData.lastLoginAt?.toDate ? firebaseUserData.lastLoginAt.toDate().toISOString() : firebaseUserData.lastLoginAt
      } as User
      
      // Store auth data
      setLocalStorage('auth_token', firebaseUser.uid)
      setLocalStorage('user_data', appUser)
      
      dispatch(loginSuccess({
        user: appUser,
        token: firebaseUser.uid,
        refreshToken: firebaseUser.uid // Firebase doesn't use separate refresh tokens like this
      }))
      
      toast.success('Hesabınız başarıyla oluşturuldu! E-posta adresinizi doğrulamayı unutmayın.')
      
      // Redirect to setup page instead of dashboard
      navigate('/setup')
    } catch (error: any) {
      const errorMessage = error.message || 'Kayıt sırasında bir hata oluştu'
      dispatch(setError(errorMessage))
      toast.error(errorMessage)
      throw error
    } finally {
      dispatch(setLoading(false))
    }
  }, [dispatch, navigate])

  // Logout function
  const logout = useCallback(async (): Promise<void> => {
    try {
      // Firebase logout
      await authService.logout()
    } catch (error) {
      // Continue with logout even if Firebase call fails
      console.warn('Firebase logout failed:', error)
    } finally {
      // Clear local storage
      removeLocalStorage('auth_token')
      removeLocalStorage('user_data')
      
      // Clear Redux state
      dispatch(logoutAction())
      
      toast.success('Başarıyla çıkış yaptınız')
      
      // Redirect to login
      navigate(ROUTES.LOGIN)
    }
  }, [dispatch, navigate])

  // Update profile function
  const updateProfile = useCallback(async (profileData: Partial<User>): Promise<void> => {
    try {
      dispatch(setLoading(true))
      
      if (!user?.id) {
        throw new Error('Kullanıcı bulunamadı')
      }
      
      // Update user data in Firestore
      await userService.updateUserData(user.id, profileData)
      
      // Get updated user data
      const updatedUserData = await userService.getUserData(user.id)
      
      if (updatedUserData) {
        const updatedUser: User = {
          ...updatedUserData
        } as User
        
        // Update local storage
        setLocalStorage('user_data', updatedUser)
        
        // Update Redux state
        dispatch(updateUser(updatedUser))
        
        toast.success('Profil başarıyla güncellendi')
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Profil güncelleme sırasında bir hata oluştu'
      dispatch(setError(errorMessage))
      toast.error(errorMessage)
      throw error
    } finally {
      dispatch(setLoading(false))
    }
  }, [dispatch, user?.id])

  // Change password function
  const changePassword = useCallback(async (
    currentPassword: string, 
    newPassword: string
  ): Promise<void> => {
    try {
      dispatch(setLoading(true))
      
      const currentUser = auth.currentUser
      if (!currentUser || !currentUser.email) {
        throw new Error('Kullanıcı oturumu bulunamadı')
      }
      
      // Re-authenticate user with current password
      await authService.reauthenticate(currentUser.email, currentPassword)
      
      // Update password in Firebase
      await authService.updateUserPassword(newPassword)
      
      toast.success('Şifre başarıyla değiştirildi')
    } catch (error: any) {
      const errorMessage = error.message || 'Şifre değiştirilirken bir hata oluştu'
      dispatch(setError(errorMessage))
      toast.error(errorMessage)
      throw error
    } finally {
      dispatch(setLoading(false))
    }
  }, [dispatch])

  // Forgot password function
  const forgotPassword = useCallback(async (email: string): Promise<void> => {
    try {
      dispatch(setLoading(true))
      
      // Firebase password reset
      await authService.resetPassword(email)
      
      toast.success('Şifre sıfırlama bağlantısı e-posta adresinize gönderildi')
    } catch (error: any) {
      const errorMessage = error.message || 'Şifre sıfırlama sırasında bir hata oluştu'
      dispatch(setError(errorMessage))
      toast.error(errorMessage)
      throw error
    } finally {
      dispatch(setLoading(false))
    }
  }, [dispatch])

  // Reset password function (Firebase handles this via email link)
  const resetPassword = useCallback(async (newPassword: string): Promise<void> => {
    try {
      dispatch(setLoading(true))
      
      const currentUser = auth.currentUser
      if (!currentUser) {
        throw new Error('Kullanıcı oturumu bulunamadı')
      }
      
      // Update password in Firebase
      await authService.updateUserPassword(newPassword)
      
      toast.success('Şifre başarıyla güncellendi')
    } catch (error: any) {
      const errorMessage = error.message || 'Şifre güncelleme sırasında bir hata oluştu'
      dispatch(setError(errorMessage))
      toast.error(errorMessage)
      throw error
    } finally {
      dispatch(setLoading(false))
    }
  }, [dispatch])

  // Verify email function
  const verifyEmail = useCallback(async (): Promise<void> => {
    try {
      dispatch(setLoading(true))
      
      const currentUser = auth.currentUser
      if (!currentUser) {
        throw new Error('Kullanıcı oturumu bulunamadı')
      }
      
      // Send email verification
      // Email verification is handled by Firebase automatically during registration
      
      toast.success('Doğrulama e-postası gönderildi')
    } catch (error: any) {
      const errorMessage = error.message || 'E-posta doğrulama sırasında bir hata oluştu'
      dispatch(setError(errorMessage))
      toast.error(errorMessage)
      throw error
    } finally {
      dispatch(setLoading(false))
    }
  }, [dispatch])

  // Add XP function
  const addUserXP = useCallback(async (amount: number, reason?: string): Promise<void> => {
    try {
      if (!user?.id) return
      
      // Update XP in Firestore
      await userService.updateUserData(user.id, {
        xp: (user.xp || 0) + amount
      })
      
      // Get updated user data
      const updatedUserData = await userService.getUserData(user.id)
      
      if (updatedUserData) {
        const updatedUser: User = {
          ...updatedUserData
        } as User
        
        // Update local storage
        setLocalStorage('user_data', updatedUser)
        
        // Update Redux state
        dispatch(updateUser(updatedUser))
        
        if (reason) {
          toast.success(`+${amount} XP - ${reason}`)
        } else {
          toast.success(`+${amount} XP kazandınız!`)
        }
      }
    } catch (error: any) {
      console.error('XP ekleme hatası:', error)
    }
  }, [user, dispatch])

  // Clear error function
  const clearAuthError = useCallback((): void => {
    dispatch(clearError())
  }, [dispatch])

  // Check if user has permission
  const hasPermission = useCallback((permission: string): boolean => {
    if (!user || !user.permissions) return false
    return user.permissions.includes(permission)
  }, [user])

  // Check if user has role
  const hasRole = useCallback((role: string): boolean => {
    if (!user || !user.roles) return false
    return user.roles.includes(role)
  }, [user])

  // Check if email is verified
  const isEmailVerified = useCallback((): boolean => {
    return user?.emailVerified || false
  }, [user])

  // Get auth token
  const getAuthToken = useCallback(async (): Promise<string | null> => {
    try {
      const currentUser = auth.currentUser
      if (currentUser) {
        return await currentUser.getIdToken()
      }
      return null
    } catch (error) {
      console.error('Error getting auth token:', error)
      return null
    }
  }, [])

  // Validate auth state consistency
  const validateAuthState = useCallback((): boolean => {
    const currentUser = auth.currentUser
    const userData = getLocalStorage('user_data', null)
    const authToken = getLocalStorage('auth_token', null)
    
    // Check if all auth components are consistent
    const hasFirebaseUser = !!currentUser
    const hasLocalUserData = !!userData
    const hasLocalToken = !!authToken
    const isReduxAuthenticated = isAuthenticated
    
    // All should be true for a valid auth state
    if (hasFirebaseUser && hasLocalUserData && hasLocalToken && isReduxAuthenticated) {
      return currentUser.uid === userData.id && currentUser.uid === authToken
    }
    
    // All should be false for a valid non-auth state
    if (!hasFirebaseUser && !hasLocalUserData && !hasLocalToken && !isReduxAuthenticated) {
      return true
    }
    
    // Inconsistent state detected
    return false
  }, [isAuthenticated])

  // Check auth status
  const checkAuthStatus = useCallback(async (): Promise<void> => {
    try {
      console.log('🔍 Checking auth status...')
      const currentUser = auth.currentUser
      const userData = getLocalStorage('user_data', null)
      const authToken = getLocalStorage('auth_token', null)
      
      if (currentUser && userData && authToken === currentUser.uid) {
        console.log('✅ User authenticated, verifying token...')
        // User is authenticated, verify token is still valid
        try {
          await currentUser.getIdToken(true) // Force refresh
          console.log('✅ Token is valid, maintaining session')
          if (!isAuthenticated) {
            // Ensure userData is serializable
            const serializedUserData = {
              ...userData,
              createdAt: typeof userData.createdAt === 'object' && userData.createdAt?.toDate ? userData.createdAt.toDate().toISOString() : userData.createdAt,
              lastLoginAt: typeof userData.lastLoginAt === 'object' && userData.lastLoginAt?.toDate ? userData.lastLoginAt.toDate().toISOString() : userData.lastLoginAt
            }
            dispatch(loginSuccess({ 
              user: serializedUserData, 
              token: currentUser.uid, 
              refreshToken: currentUser.uid 
            }))
          }
        } catch (tokenError) {
          console.error('❌ Token is invalid:', tokenError)
          // Token is invalid, clear auth data
          removeLocalStorage('auth_token')
          removeLocalStorage('user_data')
          dispatch(logoutAction())
        }
      } else if (!currentUser) {
        console.log('👋 No current user, clearing session')
        // No current user, clear any stale data
        if (userData || authToken) {
          removeLocalStorage('auth_token')
          removeLocalStorage('user_data')
        }
        if (isAuthenticated) {
          dispatch(logoutAction())
        }
      } else {
        console.log('⚠️ Inconsistent auth state, clearing data')
        // Inconsistent state, clear everything
        removeLocalStorage('auth_token')
        removeLocalStorage('user_data')
        if (isAuthenticated) {
          dispatch(logoutAction())
        }
      }
    } catch (error) {
      console.error('❌ Auth status check failed:', error)
      // Only clear auth data if we're sure there's an issue
      const currentUser = auth.currentUser
      if (!currentUser) {
        console.log('❌ No current user after error, clearing auth data')
        removeLocalStorage('auth_token')
        removeLocalStorage('user_data')
        if (isAuthenticated) {
          dispatch(logoutAction())
        }
      } else {
        console.log('✅ Current user exists despite error, keeping session')
      }
    } finally {
      // Always set loading to false after checking auth status
      dispatch(setLoading(false))
    }
  }, [dispatch, isAuthenticated])

  // Redux Persist rehydration listener
  useEffect(() => {
    let isRehydrated = false
    
    const handleRehydrate = () => {
      isRehydrated = true
    }
    
    // Store'dan rehydration durumunu kontrol et
    const checkRehydration = () => {
      const state = store.getState()
      if (state._persist?.rehydrated) {
        isRehydrated = true
      }
    }
    
    checkRehydration()
    
    // REHYDRATE action'ını dinle
    const unsubscribeStore = store.subscribe(() => {
      const state = store.getState()
      if (state._persist?.rehydrated && !isRehydrated) {
        handleRehydrate()
      }
    })
    
    return () => {
      unsubscribeStore()
    }
  }, [dispatch])

  // Firebase auth state listener with Redux Persist synchronization
  useEffect(() => {
    let isMounted = true
    let authCheckTimeout: NodeJS.Timeout | null = null
    
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!isMounted) return
      
      // Clear any existing timeout
      if (authCheckTimeout) {
        clearTimeout(authCheckTimeout)
      }
      
      // Redux Persist rehydration'ının tamamlanmasını bekle
      const waitForRehydration = () => {
        return new Promise<void>((resolve) => {
          const checkRehydration = () => {
            const state = store.getState()
            if (state._persist?.rehydrated) {
              resolve()
            } else {
              setTimeout(checkRehydration, 50)
            }
          }
          checkRehydration()
        })
      }
      
      await waitForRehydration()
      
      // Add small delay to ensure Redux Persist rehydration is complete
      authCheckTimeout = setTimeout(async () => {
        if (!isMounted) return
        
        try {
          if (firebaseUser) {
            // User is signed in
            const existingUserData = getLocalStorage('user_data', null)
            const existingToken = getLocalStorage('auth_token', null)
            
            // Check if we have valid cached data and Redux state is not already authenticated
            if (existingUserData && existingUserData.id === firebaseUser.uid && existingToken === firebaseUser.uid) {
              // If cached data is missing role/roles, refresh from Firestore to heal state
              const needsRoleHealing = (typeof existingUserData.role === 'undefined') || !Array.isArray(existingUserData.roles)
              if (needsRoleHealing) {
                try {
                  const freshUserData = await userService.getUserData(firebaseUser.uid)
                  if (!isMounted) return
                  if (freshUserData) {
                    const normalizedRoles = Array.isArray(freshUserData.roles)
                      ? freshUserData.roles
                      : (freshUserData.role ? [freshUserData.role] : ['user'])

                    const healedUser: User = {
                      ...freshUserData,
                      role: freshUserData.role,
                      roles: normalizedRoles,
                      createdAt: typeof freshUserData.createdAt === 'object' && freshUserData.createdAt?.toDate ? freshUserData.createdAt.toDate().toISOString() : freshUserData.createdAt,
                      lastLoginAt: typeof freshUserData.lastLoginAt === 'object' && freshUserData.lastLoginAt?.toDate ? freshUserData.lastLoginAt.toDate().toISOString() : freshUserData.lastLoginAt
                    } as User

                    setLocalStorage('auth_token', firebaseUser.uid)
                    setLocalStorage('user_data', healedUser)

                    dispatch(loginSuccess({ 
                      user: healedUser, 
                      token: firebaseUser.uid, 
                      refreshToken: firebaseUser.uid 
                    }))
                    return
                  }
                } catch (healingError) {
                  console.warn('Auth state healing failed:', healingError)
                }
              }

              // Use cached data - always update Redux state to ensure consistency
              const serializedUserData = {
                ...existingUserData,
                createdAt: typeof existingUserData.createdAt === 'object' && existingUserData.createdAt?.toDate ? existingUserData.createdAt.toDate().toISOString() : existingUserData.createdAt,
                lastLoginAt: typeof existingUserData.lastLoginAt === 'object' && existingUserData.lastLoginAt?.toDate ? existingUserData.lastLoginAt.toDate().toISOString() : existingUserData.lastLoginAt
              }
              dispatch(loginSuccess({ 
                user: serializedUserData, 
                token: firebaseUser.uid, 
                refreshToken: firebaseUser.uid 
              }))
            } else {
              // Fetch fresh data from Firestore
              try {
                const userData = await userService.getUserData(firebaseUser.uid)
                
                if (!isMounted) return
                
                if (userData) {
                  const normalizedRoles = Array.isArray(userData.roles)
                    ? userData.roles
                    : (userData.role ? [userData.role] : ['user'])

                  const appUser: User = {
                    ...userData,
                    role: userData.role,
                    roles: normalizedRoles, // Admin role desteği
                    createdAt: typeof userData.createdAt === 'object' && userData.createdAt?.toDate ? userData.createdAt.toDate().toISOString() : userData.createdAt,
                    lastLoginAt: typeof userData.lastLoginAt === 'object' && userData.lastLoginAt?.toDate ? userData.lastLoginAt.toDate().toISOString() : userData.lastLoginAt
                  } as User
                  
                  // Store auth data
                  setLocalStorage('auth_token', firebaseUser.uid)
                  setLocalStorage('user_data', appUser)
                  
                  dispatch(loginSuccess({ 
                    user: appUser, 
                    token: firebaseUser.uid, 
                    refreshToken: firebaseUser.uid 
                  }))
                } else {
                  // User data not found in Firestore
                  removeLocalStorage('auth_token')
                  removeLocalStorage('user_data')
                  dispatch(logoutAction())
                }
              } catch (firestoreError) {
                console.error('Firestore error:', firestoreError)
                // Keep existing session if we have cached data
                if (existingUserData && existingUserData.id === firebaseUser.uid) {
                  // Ensure fallback userData is serializable
                  const serializedUserData = {
                    ...existingUserData,
                    createdAt: typeof existingUserData.createdAt === 'object' && existingUserData.createdAt?.toDate ? existingUserData.createdAt.toDate().toISOString() : existingUserData.createdAt,
                    lastLoginAt: typeof existingUserData.lastLoginAt === 'object' && existingUserData.lastLoginAt?.toDate ? existingUserData.lastLoginAt.toDate().toISOString() : existingUserData.lastLoginAt
                  }
                  dispatch(loginSuccess({ 
                    user: serializedUserData, 
                    token: firebaseUser.uid, 
                    refreshToken: firebaseUser.uid 
                  }))
                 } else {
                   removeLocalStorage('auth_token')
                   removeLocalStorage('user_data')
                   dispatch(logoutAction())
                 }
              }
            }
          } else {
            // No user is signed in
            removeLocalStorage('auth_token')
            removeLocalStorage('user_data')
            dispatch(logoutAction())
          }
        } catch (error) {
          console.error('Auth state error:', error)
        }
      }, 100)
    })

     return () => {
       isMounted = false
       if (authCheckTimeout) {
         clearTimeout(authCheckTimeout)
       }
       unsubscribe()
     }
   }, [dispatch])

  // Refresh token function
  const refreshToken = useCallback(async (): Promise<void> => {
    try {
      const currentUser = auth.currentUser
      if (currentUser) {
        await currentUser.getIdToken(true) // Force refresh
        console.log('✅ Token refreshed successfully')
      }
    } catch (error) {
      console.error('❌ Failed to refresh token:', error)
      throw error
    }
  }, [])

  return {
    // State
    user,
    isAuthenticated,
    loading,
    error,
    
    // Actions
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    forgotPassword,
    resetPassword,
    verifyEmail,
    refreshToken,
    addUserXP,
    clearAuthError,
    
    // Utilities
    hasPermission,
    hasRole,
    isEmailVerified,
    getAuthToken,
    checkAuthStatus
  }
}

export { useAuth }
export default useAuth